import { Fragment, useEffect, useRef, useState } from "react";

import { useApolloClient } from "@apollo/client";

import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_PLAN, { UPDATE_PLAN } from "../../provider/Plans/mutation";
import { PLANS } from "../../provider/Plans/query";

/** utils */
import { getGraphsqErros, maskIntBeautify, maskRealBeautify, toFloat } from "../../utilities/functions";
import { useUser } from "../../context/users";
import FRANCHISE_REGIONS from "../../provider/Franchises/Regions/query";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";

const Crud = () => {
  const settings = useSettings();
  const apollo = useApolloClient();

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;

  const toast = useRef<any>(null);
  const dt = useRef<any>(null);

  const [records, setRecords] = useState<iPlan[]>([]);
  const [record, setRecord] = useState<iPlan>();

  const [franchise, setFranchise] = useState<any>(null);
  const [franchises, setFranchises] = useState<iFranchise[]>([]);
  const [region, setRegion] = useState<any>(null);
  const [regions, setRegions] = useState<iRegion[]>([]);
  const [load, setLoad] = useState(true);
  const [reload, setReload] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [recordDialog, setRecordDialog] = useState<boolean>(false);
  const [selectedRecords, setSelectedRecords] = useState<any>(null);
  const [deleteRecordDialog, setDeleteRecordDialog] = useState<boolean>(false);
  const [deleteRecordsDialog, setDeleteRecordsDialog] = useState<boolean>(false);

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState<any>(null);

  // const [rush_hour, setRush_hour] = useState<iRushHour[]>([])

  let emptyRecord: iPlan = {
    name: "",
    description: "",
    requests: "0",
    value: "0,00",
    is_actived: true,
  };

  if (franchise_id) emptyRecord.franchise_id = franchise_id;
  if (acting_region_id) emptyRecord.acting_region_id = acting_region_id;

  const loadRecords = (cleanRecord: boolean = false) => {
    setLoading(true);

    const variables: any = {}

    if (franchise?.code) variables.franchise_id = Number(franchise.code);
    if (region?.code) variables.acting_region_id = Number(region.code);

    if (franchise_id) variables.franchise_id = Number(franchise_id);
    if (acting_region_id) variables.acting_region_id = Number(acting_region_id);

    apollo
      .query({
        query: PLANS,
        variables,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.plans;

        setRecords(data);
        setLoading(false);
      })
      .catch(error => {
        setRecords([]);
        console.log('PLANS', JSON.stringify(error));
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setLoading(false);
      });
  };

  const loadFranchises = (_useLoad: boolean = true) => {
    if (_useLoad) setLoad(true);
    
    const filter: any = {};
  
    if (franchise_id) filter.franchise_id = Number(franchise_id);

    if (franchise?.code) filter.franchise_id = Number(franchise.code);

    apollo
      .query({
        query: FRANCHISE_REGIONS,
        variables: {
          ...filter,
        },
        fetchPolicy: "no-cache",
      })
      .then(response => {
        setFranchises(response.data.franchises || []);
        setRegions(response.data.regions || []);

        setLoad(false);
        setReload(false);
      })
      .catch(error => {
        console.log('FRANCHISE_REGIONS error', JSON.stringify(error));
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setLoad(false);
        setReload(false);
      });
  }

  const openNew = () => {
    setRecord(emptyRecord);
    setSubmitted(false);
    setRecordDialog(true);
  };

  const hideDialog = () => {
    setSubmitted(false);
    setRecordDialog(false);
  };

  const hideDeleteRecordDialog = () => {
    setDeleteRecordDialog(false);
  };

  const hideDeleteRecordsDialog = () => {
    setDeleteRecordsDialog(false);
  };

  const saveRecord = () => {
    setSubmitted(true);

    if (!record?.name || !record.description || !record?.value) {
      return;
    }

    const _record: iPlan = { ...record };

    _record.value = parseFloat(`${_record.value}`) * 100;
    _record.requests = parseInt(`${_record.requests}`);

    delete _record.__typename;
    delete _record.franchise;
    delete _record.actingRegion;

    setLoading(true);
    let mutation = CREATE_PLAN;

    if (record?.id) {
      _record.id = parseInt(`${_record.id}`);
      mutation = UPDATE_PLAN;
    }

    if (franchise?.code) _record.franchise_id = Number(franchise.code);
    if (region?.code) _record.acting_region_id = Number(region.code);

    if (franchise_id) _record.franchise_id = Number(franchise_id);
    if (acting_region_id) _record.acting_region_id = Number(acting_region_id);

    apollo
      .mutate({
        mutation,
        variables: {
          data: {
            ..._record,
          },
        },
      })
      .then(response => {
        setLoading(false);

        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

        loadRecords();
        setRecordDialog(false);
        setRecord(emptyRecord);
      })
      .catch(error => {
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
        console.log('PLAN error', error);
      });
  };

  const editRecord = (record: iPlan) => {
    const _record: iPlan = { ...record };

    _record.value = maskRealBeautify(parseFloat(`${_record?.value}`) / 100, true);

    setFranchise({ code: _record.franchise?.id, name: _record.franchise?.name });
    setRegion({ code: _record.actingRegion?.id, name: _record.actingRegion?.name });
    setRecord({ ..._record });
    setRecordDialog(true);
  };

  const confirmDeleteRecord = (record: any) => {
    setRecord(record);
    setDeleteRecordDialog(true);
  };

  const deleteRecord = () => {
    apollo
      .mutate({
        mutation: UPDATE_PLAN,
        variables: {
          data: {
            id: parseInt(`${record?.id}`),
            is_deleted: true,
          },
        },
      })
      .then(response => {
        let _records = records.filter((val: any) => val.id !== record?.id);
        setRecords(_records);
        setDeleteRecordDialog(false);
        setRecord(emptyRecord);
        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
      })
      .catch(error => {
        console.log('Delete Plan error:', JSON.stringify(error));
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const confirmDeleteSelected = () => {
    setDeleteRecordsDialog(true);
  };

  const deleteSelectedRecords = () => {
    let _records = records.filter((val: any) => !selectedRecords.includes(val));
    setRecords(_records);
    setDeleteRecordsDialog(false);
    setSelectedRecords(null);
    toast.current.show({ severity: "success", summary: "Successful", detail: "Records Deleted", life: 3000 });
  };

  const onRoleChange = (e: any) => {
    let _record: any = { ...record };
    _record.is_actived = e.value;
    setRecord(_record);
  };

  const onMonthlyChange = (e: any) => {
    let _record: any = { ...record };
    _record.monthly = e.value;
    setRecord(_record);
  };

  const onInputChange = (e: any, name: string) => {
    let val = (e.target && e.target.value) || "";

    if (name === "value") val = maskRealBeautify(val);

    if (
      name === 'passenger'
    ) val = e.value;

    let _record: any = { ...record };
    _record[name] = val;

    setRecord(_record);
  };

  const leftToolbarTemplate = () => {
    return (
      <Fragment>
        <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
        {/* <Button
          label="Delete"
          icon="pi pi-trash"
          className="p-button-danger mb-2"
          onClick={confirmDeleteSelected}
          disabled={!selectedRecords || !selectedRecords.length}
        /> */}
      </Fragment>
    );
  };

  const codeBodyTemplate = (rowData: iPlan) => {
    return (
      <>
        <span className="p-column-title">Cód.</span>
        {rowData.id}
      </>
    );
  };

  const nameBodyTemplate = (rowData: iPlan) => {
    return (
      <>
        <span className="p-column-title">Nome</span>
        {rowData.name}
      </>
    );
  };

  // const requestBodyTemplate = (rowData: iPlan) => {
  //   return (
  //     <>
  //       <span className="p-column-title">Qtde Corridas</span>
  //       {maskIntBeautify(rowData.requests)}
  //     </>
  //   );
  // };

  const valueBodyTemplate = (rowData: iPlan) => {
    return (
      <>
        <span className="p-column-title">Valor</span>
        {maskRealBeautify(parseFloat(`${rowData?.value}`) / 100, true, "R$")}
      </>
    );
  };

  const franchiseBodyTemplate = (rowData: iServices) => {
    return (
      <>
        <span className="p-column-title">Franquia</span>
        {rowData.franchise?.name}
      </>
    );
  };

  const actingRegionBodyTemplate = (rowData: iUser) => {
    return (
      <>
        <span className="p-column-title">Região</span>
        {rowData.actingRegion?.name}
      </>
    );
  };

  const statusBodyTemplate = (rowData: iPlan) => {
    return (
      <>
        <span className="p-column-title">Situação</span>
        <span className={`product-badge status-${!rowData.is_actived ? "disabled" : "actived"}`}>{rowData.is_actived ? "Ativo" : "Inativo"}</span>
      </>
    );
  };

  const actionBodyTemplate = (rowData: iPlan) => {
    return (
      <div className="actions">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-0" onClick={() => confirmDeleteRecord(rowData)} />
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">{loading ? "Carregando planos..." : "Planos/Pacotes"}</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
      </span>
    </div>
  );

  const recordDialogFooter = (
    <>
      <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
      <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
    </>
  );

  const deleteRecordDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
    </>
  );

  const deleteRecordsDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={hideDeleteRecordsDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteSelectedRecords} />
    </>
  );

  useEffect(() => {
    loadRecords(true);
    
    loadFranchises();
  }, []);

  useEffect(() => {
    if (franchise?.code){
      setRegion(null);
      setReload(true);
      loadFranchises(false);
      loadRecords(true);
    }
  }, [franchise?.code]);

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <Toast ref={toast} />

          {franchise_id && acting_region_id ? null : (
            load ? (
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
            ) : (
              <>
                <div className="card">
                  <div className="flex flex-column md:flex-row flex-wrap">
                    { !franchise_id ? (
                      <div className="flex flex-column md:flex-row flex-wrap">
                        <label htmlFor="name">Franquia</label>
                        <div className="field col-12 md:col-12">
                          <Dropdown
                            value={franchise}
                            onChange={e => setFranchise(e.value)}
                            options={franchises.map(i => ({ code: i.id, name: i.name }))}
                            optionLabel="name"
                            placeholder="Selecione uma Franquia"
                          />
                        </div>
                      </div>
                    ) : null }
      
      
                    { !acting_region_id ? (
                      <div className="flex flex-column md:flex-row flex-wrap">
                        <label htmlFor="name">Região</label>
                        <div className="field col-12 md:col-12">
                          {reload ? (
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                          ) : (
                            <Dropdown
                              value={region}
                              onChange={e => setRegion(e.value)}
                              options={regions.map(i => ({ code: i.id, name: i.name }))}
                              optionLabel="name"
                              placeholder="Selecione uma Região"
                            />
                          )}
                        </div>
                      </div>
                    ) : null }
                  </div>
                </div>
              </>
            )
          )}

          {loading ? (<i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />) : (
            <>
              <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

              <DataTable
                ref={dt}
                value={records}
                dataKey="id"
                paginator
                stripedRows={true}
                rowHover
                showGridlines
                rows={10}
                rowsPerPageOptions={[5, 10, 25]}
                className="datatable-responsive"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
                globalFilter={globalFilter}
                emptyMessage="Nenhum registro encontrado"
                header={header}
                responsiveLayout="scroll">
                <Column field="id" header="Cód." sortable body={codeBodyTemplate} headerStyle={{}}></Column>
                <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}}></Column>
                {/* <Column field="requests" header="Qtde Serviços" body={requestBodyTemplate} sortable headerStyle={{}}></Column> */}
                <Column field="value" header="Valor" body={valueBodyTemplate} sortable headerStyle={{}}></Column>
                { !franchise_id ? (
                  <Column field="franchise.name" header="Franquia" sortable body={franchiseBodyTemplate} headerStyle={{}} />
                ) : null }
                { !acting_region_id ? (
                  <Column field="actingRegion.name" header="Região" sortable body={actingRegionBodyTemplate} headerStyle={{}} />
                ) : null }

                <Column field="is_actived" header="Situação" body={statusBodyTemplate} sortable headerStyle={{}}></Column>
                <Column header="Ações" className="flex flex-row align-items-center justify-content-end" body={actionBodyTemplate}></Column>
              </DataTable>
            </>
          )}

          {/* Modal adicionar editar */}
          <Dialog
            visible={recordDialog}
            style={{ width: "550px" }}
            header={record?.id ? "Detalhes do Plano/Pacote" : "Novo Plano/Pacote"}
            modal
            className="p-fluid"
            footer={recordDialogFooter}
            onHide={hideDialog}>
            <div className="flex flex-column md:flex-row flex-wrap">

              { !franchise_id ? (
                <div className="flex flex-column md:flex-row flex-wrap">
                  <div className="field col-12 md:col-12">
                    <label htmlFor="name">Franquia</label>
                    <Dropdown
                      value={franchise}
                      onChange={e => setFranchise(e.value)}
                      options={franchises.map(i => ({ code: i.id, name: i.name }))}
                      optionLabel="name"
                      placeholder="Selecione uma Franquia"
                    />
                  </div>
                </div>
              ) : null }

              { !acting_region_id ? (
                <div className="flex flex-column md:flex-row flex-wrap">
                  <div className="field col-12 md:col-12">
                    <label htmlFor="name">Região</label>
                    <Dropdown
                      value={region}
                      onChange={e => setRegion(e.value)}
                      options={regions.map(i => ({ code: i.id, name: i.name }))}
                      optionLabel="name"
                      placeholder="Selecione uma Região"
                    />
                  </div>
                </div>
              ) : null }

              <div className="field col-12 md:col-6">
                <label htmlFor="name">* Nome</label>
                <InputText
                  id="name"
                  value={record?.name}
                  onChange={e => onInputChange(e, "name")}
                  required
                  autoFocus
                  className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                />
                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
              </div>

              {/* <div className="field col-12 md:col-6">
                <label htmlFor="requests">* Qtde Corridas</label>
                <InputText
                  id="requests"
                  type={"number"}
                  value={record?.requests}
                  onChange={e => onInputChange(e, "requests")}
                  required
                  className={classNames("p-3", { "p-invalid": submitted && !record?.requests })}
                />
                {submitted && !record?.requests && <small className="p-invalid">Qtde Serviço é obrigatório.</small>}
              </div> */}

              <div className="field col-12 md:col-6">
                <label htmlFor="requests">* Valor</label>
                <InputText
                  id="requests"
                  value={record?.value}
                  onChange={e => onInputChange(e, "value")}
                  required
                  className={classNames("p-3", { "p-invalid": submitted && !record?.value })}
                />
                {submitted && !record?.value && <small className="p-invalid">Valor é obrigatório.</small>}
              </div>
            </div>
            <div className="flex flex-column md:flex-row flex-wrap ">
              <div className="field col-12 md:col-12">
                <label htmlFor="description">* Descrição</label>
                <InputTextarea
                  id="description"
                  value={record?.description}
                  autoComplete={"new-password"}
                  onChange={e => onInputChange(e, "description")}
                  required
                  style={{ resize: "none" }}
                  className={classNames("p-3", { "p-invalid": submitted && !record?.description })}
                />
                {submitted && record?.description === "" && <small className="p-invalid">Descrição é obrigatório.</small>}
              </div>
            </div>

            {!record?.passenger ? <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label className="mb-3">Tipo</label>
                <div className="formgrid grid">
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role1" name="monthly" value={false} onChange={onMonthlyChange} checked={!record?.monthly} />
                    <label htmlFor="role1">Crédito</label>
                  </div>
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role2" name="monthly" value={true} onChange={onMonthlyChange} checked={record?.monthly} />
                    <label htmlFor="role2">Mensalidade</label>
                  </div>
                </div>
              </div>
            </div> : null}

            <div className="field col-12 md:col-12">
              <label htmlFor="single_race">Para Passageiro?</label>

              <InputSwitch
                checked={record?.passenger}
                onChange={e => onInputChange(e, "passenger")}
                style={{
                  marginLeft: 25,
                  verticalAlign: 'middle',
                }}
              />
            </div>

            <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label className="mb-3">Situação</label>
                <div className="formgrid grid">
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role1" name="is_actived" value={true} onChange={onRoleChange} checked={record?.is_actived} />
                    <label htmlFor="role1">Ativo</label>
                  </div>
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role2" name="is_actived" value={false} onChange={onRoleChange} checked={!record?.is_actived} />
                    <label htmlFor="role2">Inativo</label>
                  </div>
                </div>
              </div>
            </div>
          </Dialog>

          {/* Modal remover registro unico */}
          <Dialog
            visible={deleteRecordDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordDialogFooter}
            onHide={hideDeleteRecordDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && (
                <span>
                  Tem certeza de que deseja excluir <b>{record?.name}</b>?
                </span>
              )}
            </div>
          </Dialog>

          {/* Modal remover multiplos registros */}
          <Dialog
            visible={deleteRecordsDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordsDialogFooter}
            onHide={hideDeleteRecordsDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && <span>Tem certeza de que deseja excluir os registros selecionados?</span>}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Crud;
